import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Calendar,
  Clock,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock3,
  GraduationCap,
  Activity,
  Target,
  BarChart3,
  ChevronRight,
  Star,

} from 'lucide-react';
import { Onboarding } from '@/services/onboarding.service';
import { useOnboardingTaskInstances } from '@/hooks/useOnboardingTaskInstances';

interface OnboardingTimelineDialogProps {
  onboarding: Onboarding | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TimelineItem {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'pending' | 'in_progress';
  date?: string;
  icon: React.ReactNode;
  details?: string[];
}

const OnboardingTimelineDialog: React.FC<OnboardingTimelineDialogProps> = ({
  onboarding,
  open,
  onOpenChange
}) => {
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const { taskInstances, loading: tasksLoading } = useOnboardingTaskInstances(onboarding?._id);

  useEffect(() => {
    if (!onboarding) return;

    const items: TimelineItem[] = [

      {
        id: 'documentation',
        title: 'Documentation Collection',
        description: 'Personal and identity documents',
        status: onboarding.identityProofs?.aadhaarNumber && onboarding.identityProofs?.panNumber ? 'completed' : 'pending',
        icon: <FileText className="h-4 w-4" />,
        details: [
          `Aadhaar: ${onboarding.identityProofs?.aadhaarNumber ? 'Provided' : 'Pending'}`,
          `PAN: ${onboarding.identityProofs?.panNumber ? 'Provided' : 'Pending'}`,
          `Documents: ${onboarding.identityProofs?.aadhaarDocUrl && onboarding.identityProofs?.panDocUrl ? 'Uploaded' : 'Pending'}`
        ]
      },

      {
        id: 'previousEmployer',
        title: 'Previous Employer Documents',
        description: 'Employment verification documents',
        status: onboarding.previousEmployerDocuments?.expLetterUrl && onboarding.previousEmployerDocuments?.relievingLetterUrl ? 'completed' : 'pending',
        icon: <FileText className="h-4 w-4" />,
        details: [
          `Experience Letter: ${onboarding.previousEmployerDocuments?.expLetterUrl ? 'Uploaded' : 'Pending'}`,
          `Relieving Letter: ${onboarding.previousEmployerDocuments?.relievingLetterUrl ? 'Uploaded' : 'Pending'}`,
          `Resignation Acceptance: ${onboarding.previousEmployerDocuments?.resignationAcceptanceUrl ? 'Uploaded' : 'Pending'}`,
          `Salary Slips: ${[
            onboarding.previousEmployerDocuments?.firstMonthSalarySlipUrl,
            onboarding.previousEmployerDocuments?.secondMonthSalarySlipUrl,
            onboarding.previousEmployerDocuments?.thirdMonthSalarySlipUrl
          ].filter(Boolean).length}/3 uploaded`
        ]
      },

      {
        id: 'education',
        title: 'Educational Certificates',
        description: 'Academic qualification verification',
        status: onboarding.educationalCertificates && onboarding.educationalCertificates.length > 0 ? 'completed' : 'pending',
        icon: <GraduationCap className="h-4 w-4" />,
        details: [
          `Certificates: ${onboarding.educationalCertificates?.length || 0} uploaded`,
          ...(onboarding.educationalCertificates?.map(cert =>
            `${cert.degree} - ${cert.institution}`
          ) || [])
        ]
      },

      {
        id: 'tasks',
        title: 'Onboarding Tasks',
        description: 'Shared task completion and progress tracking',
        status: tasksLoading ? 'pending' :
          taskInstances.length === 0 ? 'pending' :
            taskInstances.every(task => task.status === 'completed') ? 'completed' :
              taskInstances.some(task => task.status === 'completed' || task.status === 'in_progress') ? 'in_progress' : 'pending',
        icon: <Target className="h-4 w-4" />,
        details: [
          `Shared Tasks: ${taskInstances.length}`,
          `Completed: ${taskInstances.filter(task => task.status === 'completed').length}`,
          `In Progress: ${taskInstances.filter(task => task.status === 'in_progress').length}`,
          `Pending: ${taskInstances.filter(task => !task.status || task.status === 'pending').length}`,
          `Progress: ${taskInstances.length > 0 ? Math.round((taskInstances.filter(task => task.status === 'completed').length / taskInstances.length) * 100) : 0}%`
        ]
      },

    ];

    setTimelineItems(items);
  }, [onboarding, taskInstances, tasksLoading]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'in_progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-emerald-600" />;
      case 'in_progress':
        return <Clock3 className="h-5 w-5 text-blue-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-amber-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };



  if (!onboarding) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh]">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">
                  Onboarding Timeline
                </DialogTitle>
                <DialogDescription className="sr-only">Step-by-step progress tracking for the candidate's onboarding journey.</DialogDescription>
                {onboarding?.candidate && (
                  <p className="text-sm text-gray-600">
                    {onboarding.candidate.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Completion Card */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800 mb-1">Timeline Completion</p>
                    <p className="text-2xl font-bold text-green-900">{onboarding.completionPercentage || 0}%</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Completed Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800 mb-1">Completed</p>
                    <p className="text-2xl font-bold text-blue-900">{timelineItems.filter(item => item.status === 'completed').length}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Card */}
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-800 mb-1">Pending</p>
                    <p className="text-2xl font-bold text-orange-900">{timelineItems.filter(item => item.status === 'pending').length}</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Completion Breakdown */}
          {onboarding.completionBreakdown && onboarding.completionBreakdown.length > 0 && (
            <Card className="border border-gray-100 shadow-none bg-white rounded-2xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-50">
                    <BarChart3 className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Completion Breakdown</h3>
                    <p className="text-gray-500">Detailed progress by category</p>
                  </div>
                </div>
                <div className="space-y-6">
                  {onboarding.completionBreakdown.map((breakdown, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 border border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
                          <Star className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-base">{breakdown.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-100 font-normal px-2 py-0.5">
                              {breakdown.weight}% weight
                            </Badge>
                            <span className="text-xs text-gray-400">
                              {breakdown.completedFields}/{breakdown.totalFields} fields
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-emerald-600">
                          {breakdown.completion}%
                        </div>
                        <div className="mt-2 w-56">
                          <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="absolute top-0 left-0 h-1.5 rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${breakdown.completion}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-50">
                  <Target className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Onboarding Steps</h3>
                  <p className="text-gray-600">Progress through each onboarding phase</p>
                </div>
              </div>

              <div className="space-y-6">
                {timelineItems.map((item, index) => (
                  <div key={item.id} className="relative">
                    {/* Timeline line */}
                    {index < timelineItems.length - 1 && (
                      <div className="absolute left-8 top-16 w-0.5 h-24 bg-gradient-to-b from-gray-200 to-transparent" />
                    )}

                    <div className="flex items-start gap-6">
                      {/* Timeline icon */}
                      <div className="flex-shrink-0">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center border-2 bg-transparent ${item.status === 'completed'
                            ? 'border-emerald-300'
                            : item.status === 'in_progress'
                              ? 'border-blue-300'
                              : 'border-amber-300'
                          }`}>
                          {getStatusIcon(item.status)}
                        </div>
                      </div>

                      {/* Timeline content */}
                      <div className="flex-1">
                        <Card className={`transition-all duration-300 border-0 ${item.status === 'completed'
                            ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200'
                            : item.status === 'in_progress'
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                              : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'
                          }`}>
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-4 mb-3">
                                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-50">
                                    {item.icon}
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {item.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm mt-1">
                                      {item.description}
                                    </p>
                                  </div>
                                  <Badge className={`${getStatusColor(item.status)} font-medium hover:bg-transparent`}>
                                    {item.status.replace('_', ' ').toUpperCase()}
                                  </Badge>
                                </div>
                                {/* Details - only show if expanded */}
                                {expandedItem === item.id && item.details && item.details.length > 0 && (
                                  <div className="bg-white/50 rounded-lg p-4 mt-4">
                                    <div className="space-y-2">
                                      {item.details.map((detail, detailIndex) => (
                                        <div key={detailIndex} className="flex items-center gap-3 text-sm text-gray-600">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                                          <span>{detail}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Date */}
                                {item.date && expandedItem === item.id && (
                                  <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                                    <Calendar className="h-4 w-4" />
                                    <span>{new Date(item.date).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex-shrink-0 ml-4 cursor-pointer" onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}>
                                <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${expandedItem === item.id ? 'rotate-90' : ''}`} />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>


        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTimelineDialog;
